import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { ArrowLeft, CalendarDays, Clock } from "lucide-react";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { CTABlock } from "@/components/CTABlock";
import { HeroBackground } from "@/components/HeroBackground";
import { Button } from "@/components/ui/button";
import { archivePosts, type BlogPost } from "@/data/blogPosts";
import { seoHead } from "@/components/SEOHead";
import archiveHero from "@/assets/mckay/roof-replacement.webp.asset.json";

export const Route = createFileRoute("/blog/archive")({
  head: () =>
    seoHead({
      title: "All Roofing & Solar Insights Archive | McKay",
      description:
        "The full McKay Roofing & Solar insights archive: closed solar company help, roof repair guides, solar cost breakdowns and county service pages across Southern California.",
      path: "/blog/archive",
      keywords:
        "roofing solar insights archive, closed solar company service, San Diego roof repair articles, Southern California solar service posts",
    }),
  component: BlogArchivePage,
});

const PAGE_SIZE = 12;

function formatDate(iso: string) {
  return new Date(`${iso}T12:00:00Z`).toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
    timeZone: "UTC",
  });
}

function ArchiveCard({ post }: { post: BlogPost }) {
  return (
    <Link
      to="/blog/$slug"
      params={{ slug: post.slug }}
      className="group flex h-full flex-col overflow-hidden rounded-3xl border border-border bg-card shadow-sm transition duration-300 hover:-translate-y-1 hover:border-accent hover:shadow-xl"
    >
      <div className="relative aspect-[16/10] w-full overflow-hidden">
        <img
          src={post.hero.src}
          alt={post.hero.alt}
          loading="lazy"
          className="absolute inset-0 h-full w-full object-cover transition duration-700 group-hover:scale-105"
        />
        <span className="absolute left-4 top-4 rounded-full bg-primary/90 px-3 py-1 text-[11px] font-bold uppercase tracking-wider text-primary-foreground backdrop-blur">
          {post.category}
        </span>
      </div>
      <div className="flex flex-1 flex-col p-6">
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
          <span className="inline-flex items-center gap-1.5">
            <CalendarDays className="h-3.5 w-3.5" aria-hidden="true" />
            {formatDate(post.datePublished)}
          </span>
          <span className="inline-flex items-center gap-1.5">
            <Clock className="h-3.5 w-3.5" aria-hidden="true" />
            {post.readMinutes} min read
          </span>
        </div>
        <h3 className="mt-3 font-display text-lg font-bold text-balance text-primary transition group-hover:text-accent">
          {post.h1}
        </h3>
        <p className="mt-3 line-clamp-3 flex-1 text-sm leading-relaxed text-muted-foreground">{post.metaDescription}</p>
        <span className="mt-4 text-sm font-bold text-accent">Read the post</span>
      </div>
    </Link>
  );
}

function BlogArchivePage() {
  const categories = useMemo(() => {
    const set = Array.from(new Set(archivePosts.map((p) => p.category))).sort();
    return ["All posts", ...set];
  }, []);
  const [active, setActive] = useState("All posts");
  const [page, setPage] = useState(1);

  const filtered = useMemo(
    () =>
      archivePosts
        .filter((p) => active === "All posts" || p.category === active)
        .sort((a, b) => a.h1.localeCompare(b.h1)),
    [active],
  );
  const pages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const current = Math.min(page, pages);
  const visible = filtered.slice((current - 1) * PAGE_SIZE, current * PAGE_SIZE);

  return (
    <div className="flex min-h-screen flex-col">
      <SiteHeader />
      <main className="flex-1">
        <section className="relative overflow-hidden bg-gradient-hero text-primary-foreground">
          <HeroBackground src={archiveHero.url} alt="McKay Roofing & Solar crew finishing a Southern California roof replacement" />
          <div className="relative mx-auto flex min-h-[70vh] max-w-5xl flex-col justify-center px-4 py-16 sm:px-6 sm:py-20 lg:px-8 lg:py-28">
            <h1 className="font-display text-4xl font-extrabold text-balance sm:text-5xl lg:text-6xl">
              The full roofing &amp; solar insights archive
            </h1>
            <p className="mt-5 max-w-2xl text-lg text-primary-foreground/85">
              Every article in the library: help for customers of closed solar companies, roof repair and replacement
              guides, solar and battery cost breakdowns, and county by county service pages.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Button asChild size="lg" className="btn-lift btn-shine">
                <Link to="/contact">Get My Free Estimate</Link>
              </Button>
              <Button
                asChild
                size="lg"
                variant="outline"
                className="btn-shine border-white/50 bg-white/10 text-white hover:bg-white hover:text-primary"
              >
                <Link to="/blog">
                  <ArrowLeft className="mr-2 h-4 w-4" aria-hidden="true" />
                  Back to the blog
                </Link>
              </Button>
            </div>
          </div>
        </section>

        <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8 lg:py-20">
          <div className="flex flex-wrap gap-2">
            {categories.map((c) => (
              <button
                key={c}
                type="button"
                onClick={() => {
                  setActive(c);
                  setPage(1);
                }}
                className={`rounded-full border px-4 py-2 text-sm font-bold transition ${
                  active === c
                    ? "border-accent bg-accent text-accent-foreground"
                    : "border-border bg-card text-primary hover:border-accent"
                }`}
              >
                {c}
              </button>
            ))}
          </div>

          <p className="mt-6 text-sm text-muted-foreground">
            Showing {visible.length} of {filtered.length} posts
          </p>

          <div className="mt-8 grid items-stretch gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {visible.map((p) => (
              <ArchiveCard key={p.slug} post={p} />
            ))}
          </div>

          {pages > 1 && (
            <nav aria-label="Archive pagination" className="mt-12 flex flex-wrap items-center justify-center gap-2">
              <Button
                variant="outline"
                onClick={() => setPage(Math.max(1, current - 1))}
                disabled={current === 1}
              >
                Previous
              </Button>
              {Array.from({ length: pages }, (_, i) => i + 1).map((n) => (
                <button
                  key={n}
                  type="button"
                  onClick={() => setPage(n)}
                  aria-current={n === current ? "page" : undefined}
                  className={`h-10 w-10 rounded-full text-sm font-bold transition ${
                    n === current
                      ? "bg-primary text-primary-foreground"
                      : "border border-border bg-card text-primary hover:border-accent"
                  }`}
                >
                  {n}
                </button>
              ))}
              <Button
                variant="outline"
                onClick={() => setPage(Math.min(pages, current + 1))}
                disabled={current === pages}
              >
                Next
              </Button>
            </nav>
          )}
        </div>

        <CTABlock />
      </main>
      <SiteFooter />
    </div>
  );
}
